import { createTheme as muiCreateTheme, type Theme, type ThemeOptions } from '@mui/material/styles';

import { breakpoints } from './breakpoints';
import { components } from './components';
import { motion } from './motion';
import { darkPalette, lightPalette } from './palette';
import { radii } from './radii';
import { darkShadows, lightShadows, accentShadows } from './shadows';
import { typography } from './typography';

/**
 * Augment MUI's Theme so consumers can read `theme.kozecho.motion` and
 * `theme.kozecho.accentShadows` directly via `useTheme()`.
 */
declare module '@mui/material/styles' {
  interface Theme {
    kozecho: {
      motion: typeof motion;
      accentShadows: typeof accentShadows;
      radii: typeof radii;
    };
  }
  interface ThemeOptions {
    kozecho?: {
      motion?: typeof motion;
      accentShadows?: typeof accentShadows;
      radii?: typeof radii;
    };
  }
}

export type ThemeMode = 'light' | 'dark';

function buildOptions(mode: ThemeMode): ThemeOptions {
  return {
    palette: mode === 'dark' ? darkPalette : lightPalette,
    typography,
    breakpoints,
    shape: { borderRadius: radii.md },
    spacing: 8, // MUI default; documented here for clarity
    shadows: mode === 'dark' ? darkShadows : lightShadows,
    components,
    kozecho: {
      motion,
      accentShadows,
      radii,
    },
  };
}

export const lightTheme: Theme = muiCreateTheme(buildOptions('light'));
export const darkTheme: Theme = muiCreateTheme(buildOptions('dark'));

export function getTheme(mode: ThemeMode): Theme {
  return mode === 'dark' ? darkTheme : lightTheme;
}
